/* =============================================================================
   VIEWS / ASSIGNMENT-PICKER — Tisch-Anordnungs-Dialog
   =============================================================================
   NUR Raumplan (S218 Cut 2): der Operator wählt Tische direkt auf dem Raumplan
   (renderPickerManual), mit Connectivity-Check + Zusatzstuhl-Mathematik. Die
   Empfehlung wird als orange „recommendedSet"-Markierung auf dem Plan angezeigt
   (frische Top-Option aus generateAssignmentOptions). Die frühere Vorschläge-
   Liste ('options'-Modus) + Tab-Leiste sind entfernt — ein Modus, kein Toggle.
   Übergibt das Ergebnis an commitAssignmentAndConfirm (Reservierung) bzw.
   commitWalkInTableAssignment (Walk-In).
============================================================================ */

import { htmlEscape, fmtDate } from '../utils/helpers.js';
import { closeModal } from '../utils/dom.js';
import {
  generateAssignmentOptions,
  areTablesConnected,
  naturalCompare
} from '../core/reservation-engine.js';
import {
  getCurrentSlotDuration,
  getOccupiedTableIds,
  tableExtraChairs
} from '../core/capacity.js';
// S207 — geteilter Plan-Renderer (Struktur-Elemente + Canvas-Größe) aus core/
// layout-render.js. GLEICHE Quelle wie Belegungs-Snapshot (occupancy.js) und
// Gast-Plan (customer-plan.js) — kein dritter Renderer. Cross-Refs explizit
// importiert (export*-Falle S204-HF1: bloßes Re-Export erzeugt KEINE lokale
// Bindung). table-plan.js liefert die Bereichs-/Segment-Pure-Logik.
import {
  renderElementStatic,
  elementsForArea,
  canvasSizeForArea,
  planCanvasHtml,
  contentBBox
} from '../core/layout-render.js';
import { enabledPlanAreas, areaToSegment } from '../core/table-plan.js';
import { fitAllPlans, sizePlanPanel } from '../utils/plan-fit.js';

// S207-HOTFIX 5 — Picker-Modal folgt der gecroppten Skizze (min 640 fürs Chrome:
// Tabs/Summen-Zeile/Stepper; max wie bisher). Nur der Reservierungs-Picker; der
// Walk-In-Picker sitzt im Order-Entry-Modal (eigene Größe) und wird nicht angetastet.
const _PICKER_PANEL_OPTS = { minW: 640, maxWpx: 1200, maxWvw: 96, chrome: 48 };
function _sizePickerPanel(scopeEl) {
  const ps = _pickerState;
  if (!ps || ps.walkIn || !scopeEl || !scopeEl.closest) return;
  sizePlanPanel(scopeEl.closest('.modal'), _PICKER_PANEL_OPTS);
}

function getState() {
  return (window.App && window.App.storage && window.App.storage._getInternalState())
         || { reservations: [], tables: [], settings: {} };
}

let _pickerState = null;

/**
 * S207-HOTFIX 4 / S219 Cut 2 — Vorauswahl-Weiche. Der Kern-Bug: Gast-Anfragen
 * speichern beim Anlegen IMMER einen Auto-Vorschlag in assigned_table_ids → beim
 * „Bestätigen" würde der (ggf. veraltete) gespeicherte Vorschlag vorausgewählt
 * statt frisch gerechnet.
 *  - intent='change': die ECHTE aktuelle Zuweisung behalten (nicht frisch rechnen,
 *    nicht stale überschreiben) — assignedTables, sonst wishTableId.
 *  - Walk-In MIT vorhandener assignedTables (Zurück-Navigation / Belegungs-Kachel):
 *    diese Vorauswahl hat Vorrang.
 *  - intent='confirm' ODER Walk-In OHNE Vorauswahl: FRISCHE Top-Empfehlung aus
 *    generateAssignmentOptions (mit wishTableId → per HF2-Ranking die Wunsch-
 *    Variante, wenn erfüllbar); Fallback [wishTableId], sonst leer. NICHT die
 *    gespeicherten assignedTables. Walk-In nutzt seine echte Verweildauer.
 * Pure genug für Tests (generateAssignmentOptions/getCurrentSlotDuration injizierbar
 * via Modul-Mock). KEINE Engine-Änderung — nur die Auswahl der Vorauswahl-Quelle.
 */
export function _preselectTables(r, intent, isWalkIn) {
  if (!r) return [];
  if (intent === 'change') {
    return (r.assignedTables && r.assignedTables.length)
      ? r.assignedTables.slice()
      : (r.wishTableId ? [r.wishTableId] : []);
  }
  // Walk-In mit bereits gewähltem Tisch → Vorrang (behalten, nicht überschreiben).
  if (isWalkIn && r.assignedTables && r.assignedTables.length) {
    return r.assignedTables.slice();
  }
  // confirm ODER Walk-In ohne Vorauswahl → frische Top-Empfehlung (Walk-In mit
  // echter Verweildauer, sonst Slot-Standard).
  const dur = (isWalkIn && Number(r.durationMin) > 0) ? Number(r.durationMin) : getCurrentSlotDuration();
  const opts = generateAssignmentOptions(r.date, r.time, r.guests, r.area, r.id, dur, r.wishTableId);
  if (opts && opts.length && Array.isArray(opts[0].tables) && opts[0].tables.length) {
    return opts[0].tables.slice();
  }
  return r.wishTableId ? [r.wishTableId] : [];
}

/**
 * @param {string} id      Reservierungs-ID (oder synthetische Walk-In-ID).
 * @param {string} intent  'confirm' | 'change' | 'walkin'
 * @param {object} [walkIn] S134 P2 — Walk-In-Kontext { id, date, time, guests,
 *   area, durationMin, name?, assignedTables? }. Wenn gesetzt, arbeitet der
 *   Picker reservierungsfrei und committet über commitWalkInTableAssignment.
 */
export function openAssignmentPicker(id, intent, walkIn) {
  const state = getState();
  const r = walkIn || state.reservations.find(x => x.id === id);
  if (!r) return;
  _pickerState = {
    id, intent,
    walkIn: walkIn || null,
    manualSelected: new Set(_preselectTables(r, intent, !!walkIn)),
    manualChairs: Number(r.extraChairsUsed) || (r.needsExtraChair ? 1 : 0),
    // S207-HOTFIX — „touched" nur bei bereits verbuchter Stuhl-Zahl (change/re-confirm).
    // Sonst false → die Sitzplatz-Logik wendet Zusatzstühle AUTOMATISCH an (so viele wie
    // nötig, gedeckelt auf die verfügbaren), wie die Vorschlags-Engine. Der Operator
    // kann den Wert danach frei überschreiben (setPickerChairs setzt touched=true).
    manualChairsTouched: Number(r.extraChairsUsed) > 0
  };
  // S207-HOTFIX 3 — Overlay ZUERST sichtbar schalten, dann rendern: sonst misst das
  // Fit-Scaling eine Breite von 0 (display:none) und fällt auf Skala 1 zurück.
  document.getElementById('modal-overlay').classList.add('active');
  renderAssignmentPicker();
}

/**
 * S134 P2 — Picker-State für den Walk-In-Modus vorbelegen (ohne Overlay zu
 * öffnen). Genutzt vom Hier-Essen-Flow, der den Picker als Step 3 in seinen
 * eigenen Modal-Render einbettet (`buildAssignmentPickerHtml`).
 */
export function setWalkInPickerState(walkIn) {
  // S218 Cut 2 — nur noch Raumplan. S219 Cut 2 — Vorauswahl wie bei Reservierungen:
  // ein bereits gewählter Tisch (Belegungs-Kachel / Zurück-Navigation) hat Vorrang;
  // ohne Vorauswahl wird die frische Top-Empfehlung vorausgewählt (_preselectTables,
  // isWalkIn=true). Operator kann frei ab-/umwählen (Klick-Mechanik unverändert).
  _pickerState = {
    id: (walkIn && walkIn.id) || 'walkin-pick',
    intent: 'walkin',
    walkIn: walkIn || null,
    manualSelected: new Set(_preselectTables(walkIn, 'walkin', true)),
    manualChairs: 0,
    manualChairsTouched: false   // S207-HOTFIX — Zusatzstühle automatisch anwenden (s. openAssignmentPicker)
  };
}

/** Reservierung (oder Walk-In-Kontext) zum aktuellen Picker-State auflösen. */
function _resolveReservation(ps) {
  if (!ps) return null;
  if (ps.walkIn) return ps.walkIn;
  const state = getState();
  return state.reservations.find(x => x.id === ps.id) || null;
}

/** Probe-Dauer: Walk-In-Verweildauer, sonst Reservierungs-Slot-Standard. */
function _pickerDuration(ps) {
  return (ps && ps.walkIn && Number(ps.walkIn.durationMin) > 0)
    ? Number(ps.walkIn.durationMin) : getCurrentSlotDuration();
}

/** Commit-Weiche (S220 additiv): ein detached-Kontext (ps.walkIn) kann eigene
 *  Callbacks mitbringen — `onAccept` gewinnt vor dem admin-order-entry-Default
 *  (commitWalkInTableAssignment). So legt walkin.js erst hier (beim Accept) an,
 *  ohne den Order-Entry-Flow anzufassen. Ohne Callback: alte Wege 1:1. */
function _commitPicker(tables, chairs) {
  const ps = _pickerState;
  if (!ps) return;
  if (ps.walkIn) {
    if (typeof ps.walkIn.onAccept === 'function') {
      ps.walkIn.onAccept(tables, Number(chairs) || 0);
    } else if (typeof window.commitWalkInTableAssignment === 'function') {
      window.commitWalkInTableAssignment(tables, Number(chairs) || 0);
    }
  } else if (typeof window.commitAssignmentAndConfirm === 'function') {
    window.commitAssignmentAndConfirm(ps.id, tables, Number(chairs) || 0, ps.intent);
  }
}

/* S220 — Dismiss/Back-Dispatcher: ein detached-Kontext kann onClose/onBack
   mitbringen (walkin.js: alles verwerfen bzw. zurück ins Formular). Ohne
   Callback greifen die admin-order-entry-Defaults 1:1 (× = closeAdminOrderEntry,
   Abbrechen/Zurück = adminOrderWalkInPickerBack), bei persistierter Reservierung
   schließt closeModal. Inline-Handler bleiben statisch (CSP). */
export function pickerHeaderDismiss() {
  const w = _pickerState && _pickerState.walkIn;
  if (w && typeof w.onClose === 'function') { w.onClose(); return; }
  if (w) { if (typeof window.closeAdminOrderEntry === 'function') window.closeAdminOrderEntry(); return; }
  closeModal();
}
export function pickerActionsCancel() {
  const w = _pickerState && _pickerState.walkIn;
  if (w && typeof w.onClose === 'function') { w.onClose(); return; }
  if (w) { if (typeof window.adminOrderWalkInPickerBack === 'function') window.adminOrderWalkInPickerBack(); return; }
  closeModal();
}
export function pickerBack() {
  const w = _pickerState && _pickerState.walkIn;
  if (w && typeof w.onBack === 'function') { w.onBack(); return; }
  if (typeof window.adminOrderWalkInPickerBack === 'function') window.adminOrderWalkInPickerBack();
}

/** Baut das innere Picker-HTML (Header/Meta/Tabs/Body) als String. */
export function buildAssignmentPickerHtml() {
  const ps = _pickerState;
  if (!ps) return '';
  const r = _resolveReservation(ps);
  if (!r) return '';
  // S220 — detached-Kontext (ps.walkIn) kann als ECHTE Reservierung beschriftet
  // sein (label='reservation', walkin.js Cockpit-Flow) statt als Walk-In. Nur die
  // Optik unterscheidet; Commit/Back/Close laufen über die Callback-Dispatcher.
  const isDetached = !!ps.walkIn;
  const asReservation = isDetached && ps.walkIn.label === 'reservation';
  const isWalkInLabel = isDetached && !asReservation;
  const title = ps.intent === 'change' ? 'Tisch-Zuweisung ändern'
    : isWalkInLabel ? 'Tisch wählen — 🍽️ Walk-In'
    : `Reservierung bestätigen · ${r.name}`;
  const areaLabelFn = (typeof window.areaLabel === 'function') ? window.areaLabel : (a => a || '');
  const body = renderPickerManual(r);   // S218 Cut 2 — nur noch Raumplan
  // × verwirft, „← Zurück (Gast-Daten)" geht ins Formular — beides über Dispatcher
  // (ctx-Callback vor admin-order-entry-Default; persistierte Reservierung: closeModal).
  const closeHandler = isDetached ? 'pickerHeaderDismiss()' : 'closeModal()';
  const detachedFoot = isDetached ? `
    <div class="admin-order-foot" style="margin-top:14px;">
      <button class="btn btn-ghost" onclick="pickerBack()">← Zurück (Gast-Daten)</button>
    </div>` : '';
  const metaWhen = isWalkInLabel ? 'jetzt' : `${fmtDate(r.date)} um ${r.time} Uhr`;
  return `
    <div class="modal-header">
      <h2>${htmlEscape(title)}</h2>
      <button class="modal-close" onclick="${closeHandler}">×</button>
    </div>
    <div class="picker-meta">
      <strong>${r.guests} ${r.guests === 1 ? 'Person' : 'Personen'}</strong>
      &nbsp;·&nbsp; ${metaWhen}
      ${isWalkInLabel ? `&nbsp;·&nbsp; ${r.durationMin} Min Verweildauer` : ''}
      ${r.area && r.area !== 'any' ? '&nbsp;·&nbsp; Wunsch: ' + areaLabelFn(r.area) : ''}
      ${r.wishTableId ? `<div class="picker-wish">💺 Wunschtisch: <strong>${htmlEscape((getState().tables.find(t => t.id === r.wishTableId) || {}).name || r.wishTableId)}</strong> <span style="color:var(--ink-soft);">— unverbindlich, priorisiert vorgeschlagen; Zuweisung frei.</span></div>` : ''}
      ${r.notes ? `<div class="picker-notes">📝 ${htmlEscape(r.notes)}</div>` : ''}
    </div>
    <div id="picker-body">${body}</div>
    ${detachedFoot}
  `;
}

/**
 * S207 — Nur den Picker-Body (Vorschläge- ODER Plan-Modus) neu rendern, ohne
 * Header/Meta/Tabs anzutasten (non-destruktiv). Genutzt von Toggle/Segment/
 * Zusatzstühle im Plan-Modus. Bewahrt die Scroll-Position der Plan-Fläche
 * (Tablet: großer Raum im Modal). Fällt auf den vollen Render zurück, falls
 * der Body-Container fehlt (z. B. erster Aufruf).
 */
function _rerenderPickerBody() {
  const body = document.getElementById('picker-body');
  const ps = _pickerState;
  const r = ps && _resolveReservation(ps);
  if (!body || !r) { renderAssignmentPicker(); return; }
  const scroller = body.querySelector('.picker-plan-scroll');
  const sx = scroller ? scroller.scrollLeft : 0;
  const sy = scroller ? scroller.scrollTop : 0;
  body.innerHTML = renderPickerManual(r);   // S218 Cut 2 — nur noch Raumplan
  _sizePickerPanel(body);   // S207-HOTFIX 5 — Modal-Breite an die Skizze …
  fitAllPlans(body);        // S207-HOTFIX 3 — … dann Canvas neu einpassen
  const s2 = body.querySelector('.picker-plan-scroll');
  if (s2) { s2.scrollLeft = sx; s2.scrollTop = sy; }
}

export function renderAssignmentPicker() {
  const el = document.getElementById('modal-content');
  if (!el) return;
  const ps = _pickerState;
  // Reservierungs-Modus: verschwindet die Reservierung, Modal schließen.
  // Walk-In-Modus: r kommt aus ps.walkIn, kann nicht „verschwinden".
  if (ps && !ps.walkIn && !getState().reservations.find(x => x.id === ps.id)) {
    closeModal();
    return;
  }
  el.innerHTML = buildAssignmentPickerHtml();
  _sizePickerPanel(el);   // S207-HOTFIX 5 — Modal-Breite an die Skizze …
  fitAllPlans(el);        // S207-HOTFIX 3 — … dann Canvas skalieren
}

// S204/S207 — Wunschtisch-Priorisierung ist SINGLE-SOURCE in der Engine
// (generateAssignmentOptions sortiert wish-first + Einzeltisch-Vorrang). Der Picker
// nutzt nur noch die Top-Option als Empfehlungs-Markierung (recommendedSet) auf dem
// Plan; der Operator wählt frei. Kein zweiter Re-Sort.
function _orderedAssignmentOptions(r) {
  return generateAssignmentOptions(r.date, r.time, r.guests, r.area, r.id, _pickerDuration(_pickerState), r.wishTableId);
}

function renderPickerManual(r) {
  const state = getState();
  const ps = _pickerState;
  const occupied = getOccupiedTableIds(r.date, r.time, _pickerDuration(ps), r.id);
  const selected = ps.manualSelected;
  const selectedTables = state.tables.filter(t => selected.has(t.id));
  // S207-HOTFIX — untouched (Operator hat den Stepper nicht angefasst) → null = Auto-Modus:
  // die Logik zieht so viele hinterlegte Zusatzstühle wie nötig, um die Gästezahl zu
  // decken (wie die Vorschlags-Engine). Angefasst → exakt der eingegebene Wert (clamped).
  const chairInput = ps.manualChairsTouched ? ps.manualChairs : null;
  const seatTotals = _computeManualSeatTotals(selectedTables, chairInput, r.guests, tableExtraChairs);
  const { totalSeats, maxExtraChairs, chairBonus, effective, enough } = seatTotals;
  ps.manualChairs = chairBonus;   // Auto- oder Explizit-Wert im Stepper + Commit spiegeln
  const sel = Array.from(selected);
  const selTables = sel.map(id => state.tables.find(t => t.id === id)).filter(Boolean);
  const conn = _validateManualConnectivity(selTables, sel, areTablesConnected);
  const connectivityOk = conn.ok;
  const connectivityReason = conn.reason;
  const valid = _validatePickerSelection(sel.length, enough, connectivityOk);

  // S207 — Plan-Modus: aktiver Bereich (Reservierungs-Wunsch vorgewählt, sonst erster
  // aktiver). S207-HOTFIX 4: Empfehlungs-Set = Tische der frischen Top-Option (führt
  // in kräftiger Wunsch-Optik, nicht mehr gestrichelt).
  const planAreas = enabledPlanAreas(state.settings);
  // S219-HF — Ansicht folgt der (vor)gewählten Auswahl: liegt sie in EINEM Bereich,
  // dort öffnen (macht eine nach Außen/Club verlegte Empfehlung sichtbar). Nach der
  // ersten Auflösung persistiert ps.planArea → stabil bei De-/Reselect (kein Flip).
  const selectedArea = _selectedTablesArea(ps.manualSelected, state.tables);
  const planArea = _resolvePickerPlanArea(ps, r, planAreas, selectedArea);
  ps.planArea = planArea;
  const topOpt = _orderedAssignmentOptions(r)[0];
  const recommendedSet = new Set((topOpt && Array.isArray(topOpt.tables)) ? topOpt.tables : []);

  return `
    <div class="picker-manual">
      <div class="picker-manual-status ${valid ? 'ok' : 'warn'}">
        <div><strong>${sel.length}</strong> Tisch${sel.length===1?'':'e'} ausgewählt
        &nbsp;·&nbsp; <strong>${totalSeats}${chairBonus?' + ' + chairBonus:''}</strong> = ${effective} ${effective===1?'Platz':'Plätze'}
        &nbsp;·&nbsp; benötigt: <strong>${r.guests}</strong></div>
        ${sel.length === 0 ? '<div class="picker-hint">Klick auf Tische, um sie auszuwählen.</div>' : ''}
        ${!enough && sel.length > 0 ? `<div class="picker-warn">⚠ Zu wenige Plätze (${effective}/${r.guests})</div>` : ''}
        ${!connectivityOk ? `<div class="picker-warn">⚠ ${connectivityReason}</div>` : ''}
      </div>
      <div class="picker-chair-toggle" style="display:flex; align-items:center; gap:8px; padding:8px 0;">
        <label style="font-size:.9rem;">Zusatzstühle nutzen:</label>
        <input type="number" min="0" max="${maxExtraChairs}" step="1" value="${chairBonus}"
               oninput="setPickerChairs(this.value)"
               style="width:70px;" ${maxExtraChairs === 0 ? 'disabled' : ''}>
        <span style="font-size:.8rem; color:var(--ink-soft);">max. ${maxExtraChairs} möglich (Summe der gewählten Tische)</span>
      </div>
      ${_renderPickerPlan(r, planArea, { occupied, selected, recommendedSet, areas: planAreas })}
      <div class="btn-row picker-actions">
        <button class="btn btn-primary" ${!valid?'disabled':''} onclick="acceptPickerManual()">
          ${(ps.walkIn && ps.walkIn.label !== 'reservation') ? 'Tisch übernehmen →'
            : _pickerState.intent === 'change' ? 'Übernehmen'
            : 'Bestätigen'}
        </button>
        <button class="btn btn-ghost" onclick="${ps.walkIn ? 'pickerActionsCancel()' : 'closeModal()'}">Abbrechen</button>
      </div>
    </div>
  `;
}

export function togglePickerTable(id) {
  const sel = _pickerState.manualSelected;
  if (sel.has(id)) sel.delete(id); else sel.add(id);
  _rerenderPickerBody();
}
export function setPickerChairs(val) {
  _pickerState.manualChairs = Math.max(0, parseInt(val, 10) || 0);
  _pickerState.manualChairsTouched = true;   // S207-HOTFIX — explizite Operator-Wahl gewinnt über Auto
  _rerenderPickerBody();
}
/** S207 — Plan-Bereich (Innen/Außen/Club) im Picker umschalten. Nur den
 *  Picker-Body neu rendern (non-destruktiv), Auswahl bleibt bereichsübergreifend
 *  erhalten (manualSelected wird nicht angetastet). */
export function setPickerPlanArea(area) {
  if (!_pickerState) return;
  _pickerState.planArea = area;
  _rerenderPickerBody();
}
export function acceptPickerManual() {
  const ps = _pickerState;
  const tables = Array.from(ps.manualSelected);
  if (!tables.length) return;
  _commitPicker(tables, Number(ps.manualChairs) || 0);
}

// ---------------------------------------------------------------
// S207 — Plan-Modus-Render (Operator-Sicht auf dem Raumplan)
// ---------------------------------------------------------------

/**
 * Aktiver Plan-Bereich für den Picker. Priorität:
 *   1. bereits gewählter Bereich (Segment-Klick, in ps.planArea) — Operator-Nav gewinnt
 *   2. S219-HF — Bereich der (vor)gewählten Tische (`selectedArea`): liegt die
 *      Auswahl/Empfehlung in EINEM aktiven Bereich, dort öffnen. Sonst sieht der
 *      Operator eine auto-verlegte Empfehlung außerhalb des Default-Innenbereichs
 *      nicht (Jonas-Befund: 5-P-Reservierung nach Außen verlegt, Default-View Innen).
 *   3. Reservierungs-Wunschbereich (r.area, wenn konkret)
 *   4. erster aktiver Bereich → 'indoor'
 * Rein aus den Argumenten ableitbar (testbar). `areas` = enabledPlanAreas(settings).
 * @param {{planArea?: string}} ps
 * @param {{area?: string}} r
 * @param {Array<{id: string}>} areas
 * @param {string|null} [selectedArea] gemeinsamer Bereich der gewählten Tische
 * @returns {string}
 */
function _resolvePickerPlanArea(ps, r, areas, selectedArea) {
  const enabledIds = (Array.isArray(areas) ? areas : []).map(a => a.id);
  if (ps && ps.planArea && enabledIds.includes(ps.planArea)) return ps.planArea;
  if (selectedArea && enabledIds.includes(selectedArea)) return selectedArea;
  const wish = areaToSegment(r && r.area);   // 'indoor'|'outdoor'|'club'|'any'
  if (wish !== 'any' && enabledIds.includes(wish)) return wish;
  return enabledIds[0] || 'indoor';
}

/**
 * S219-HF — gemeinsamer Bereich der gewählten Tische. Liegen ALLE gewählten Tische
 * in genau einem Bereich → dieser; keine Auswahl oder gemischte Bereiche → null
 * (dann greift die Wunsch-/Default-Weiche). Pure (Vitest). `selectedIds` = Set ODER
 * Array; `tables` = state.tables.
 * @returns {string|null}
 */
function _selectedTablesArea(selectedIds, tables) {
  const ids = (selectedIds instanceof Set) ? Array.from(selectedIds)
    : (Array.isArray(selectedIds) ? selectedIds : []);
  if (!ids.length) return null;
  const list = Array.isArray(tables) ? tables : [];
  const areas = new Set();
  for (const id of ids) {
    const t = list.find(x => x && x.id === id);
    if (t && t.area) areas.add(t.area);
  }
  return areas.size === 1 ? Array.from(areas)[0] : null;
}

/**
 * Kachel-Status aus Operator-Sicht (pure, testbar). Präzedenz (S207-HOTFIX 4):
 *   belegt   → harte Sperre, nicht wählbar (grau), NICHT diese Reservierung
 *   Empfehlung (in recommendedSet = Tische der Top-Option) → orange „Wunsch-Optik",
 *              führt die vorgewählte Empfehlung an; führt VOR gewählt (Jonas-Review:
 *              die frische Empfehlung soll einheitlich orange erscheinen)
 *   gewählt  → grün (Operator-Pick AUSSERHALB der Empfehlung)
 *   frei     → wählbar
 * `isWish` ist ein orthogonaler Marker (💺-Badge am eigentlichen Wunschtisch, der die
 * Empfehlung anführt). getOccupiedTableIds schließt die aktuelle Reservierung aus.
 * @param {{id: string, extendable?: boolean}} table
 * @param {{occupiedSet?: Set, selectedSet?: Set, wishTableId?: string, recommendedSet?: Set}} ctx
 * @returns {{status: string, selectable: boolean, occupied: boolean,
 *            selected: boolean, isRecommended: boolean, isWish: boolean, rigid: boolean}}
 */
function pickerTileStatus(table, ctx) {
  const c = ctx || {};
  const id = table && table.id;
  const inSet = (s) => (s instanceof Set) && s.has(id);
  const occupied = inSet(c.occupiedSet);
  const isRecommended = !occupied && inSet(c.recommendedSet);
  const selected = !occupied && inSet(c.selectedSet);
  const status = occupied ? 'occ'
    : isRecommended ? 'reco'
    : selected ? 'sel'
    : 'free';
  return {
    status,
    selectable: !occupied,
    occupied,
    selected,
    isRecommended,
    isWish: !!c.wishTableId && c.wishTableId === id,
    rigid: !!(table && table.extendable === false)
  };
}

/**
 * S207-HOTFIX — Kachel-Kapazität (Operator) = Sitzplätze + hinterlegte Zusatzstühle.
 * Nutzt DIESELBE Quelle wie die Sitzmathematik (tableExtraChairs) + die Auto-Stuhl-
 * Logik, damit die „XP"-Zahl auf der Kachel exakt dem entspricht, was der Tisch fassen
 * kann — sonst wirkt ein 3-Sitzer mit Zusatzstuhl (Kapazität 4) fälschlich zu klein.
 * @param {{seats?: number}} t
 * @returns {number}
 */
function _tileCapacity(t) {
  return (Number(t && t.seats) || 0) + (Number(tableExtraChairs(t)) || 0);
}

/** Operator-Kachel im Plan: geteilte Geometrie (table-node seats-N shape-X) +
 *  Status-Klassen. Inhalt = Tischname + „XP" (Operator darf den Namen sehen —
 *  anders als die datensparsame Gast-„XP"-Only-Regel). „XP" = Max-Kapazität
 *  inkl. hinterlegter Zusatzstühle (S207-HOTFIX). */
function _renderPickerTile(t, r, ctx) {
  const st = pickerTileStatus(t, {
    occupiedSet: ctx.occupied,
    selectedSet: ctx.selected,
    wishTableId: r.wishTableId,
    recommendedSet: ctx.recommendedSet
  });
  // S207-HOTFIX 4 — Empfehlungs-Tische (Top-Option) = kräftige Wunsch-Optik (orange,
  // pt-reco); die gestrichelte „Vorschlag"-Optik entfällt. Der Wunschtisch behält
  // sein 💺 (inline). fix/kombinierbar hat keine visuelle Unterscheidung (HF2).
  const cls = ['table-node',
    `seats-${Math.min(12, Number(t.seats) || 1)}`,
    `shape-${t.shape || 'rectangular'}`,
    'picker-plan-tile', `pt-${st.status}`].filter(Boolean).join(' ');
  const click = st.selectable ? `onclick="togglePickerTable('${htmlEscape(t.id)}')"` : '';
  const title = st.occupied ? 'belegt/verplant im Slot'
    : st.isWish ? 'Wunschtisch des Gastes'
    : st.isRecommended ? 'Empfehlung'
    : 'wählbar';
  return `
    <div class="${cls}" style="left:${Number(t.x) || 0}px; top:${Number(t.y) || 0}px; cursor:${st.selectable ? 'pointer' : 'default'};"
         ${click} title="${htmlEscape(title)}" aria-disabled="${st.selectable ? 'false' : 'true'}">
      <div class="tname">${htmlEscape(t.name)}</div>
      <div class="tile-status-line${st.occupied ? '' : ' free'}">${st.isWish ? '💺 ' : ''}${st.occupied ? 'belegt' : `${_tileCapacity(t)}P`}</div>
    </div>`;
}

/** Plan-Canvas im Picker: 3er-Segment (aktive Bereiche) + Struktur-Elemente
 *  statisch (geteilter Renderer) + Operator-Tisch-Kacheln. Legende oben. */
function _renderPickerPlan(r, planArea, ctx) {
  const state = getState();
  const size = canvasSizeForArea(state, planArea);
  const els = elementsForArea(state, planArea);
  const elLayer = els.map(renderElementStatic).join('');
  const tables = (state.tables || [])
    .filter(t => t.area === planArea && t.disabled !== true)
    .sort((x, y) => naturalCompare(x.name, y.name));
  const seg = `<div class="occ-seg picker-plan-seg" role="tablist" aria-label="Bereich">${ctx.areas.map(a =>
    `<button type="button" class="occ-seg-btn${a.id === planArea ? ' active' : ''}" role="tab"
       aria-selected="${a.id === planArea ? 'true' : 'false'}"
       onclick="setPickerPlanArea('${a.id}')">${htmlEscape(a.label)}</button>`).join('')}</div>`;
  // S207-HOTFIX 4 — Legende: „Empfehlung" (orange) statt gestrichelter „Vorschlag".
  const legend = `
    <div class="picker-plan-legend">
      <span><i class="pl-key pl-free"></i>frei</span>
      <span><i class="pl-key pl-sel"></i>gewählt</span>
      <span><i class="pl-key pl-reco"></i>Empfehlung</span>
      <span><i class="pl-key pl-reco"></i>💺 Wunsch</span>
      <span><i class="pl-key pl-occ"></i>belegt</span>
    </div>`;
  const tilesHtml = tables.length
    ? tables.map(t => _renderPickerTile(t, r, ctx)).join('')
    : `<p class="tc-hint" style="padding:14px; color:var(--ink-soft);">Keine Tische in diesem Bereich.</p>`;
  // S207-HOTFIX 3+4 — fit-skalierbarer Canvas, gecroppt auf den Inhalt (kein toter Rand,
  // gedrehte Elemente eingerechnet); Degenerat-Fallback = volle Canvas.
  const bbox = contentBBox(els, tables, 24) || { x: 0, y: 0, w: size.w, h: size.h };
  return `
    ${seg}
    ${legend}
    ${planCanvasHtml({
      canvasW: bbox.w, canvasH: bbox.h, cropX: bbox.x, cropY: bbox.y,
      outerClass: 'picker-plan-scroll', innerClass: 'picker-plan-canvas',
      innerHtml: elLayer + tilesHtml
    })}`;
}

// ---------------------------------------------------------------
// Pure helpers (extrahiert in Session 39 / B.4 für Test-Coverage)
// ---------------------------------------------------------------

/**
 * Pure-Logic für die Connectivity-Validierung im Manual-Modus.
 * @param {Array<{area: string, extendable?: boolean}>} selectedTables
 * @param {string[]} selectedIds zur Übergabe an areTablesConnectedFn
 * @param {(ids: string[]) => boolean} areTablesConnectedFn injizierbar
 * @returns {{ok: boolean, reason: string}}
 */
function _validateManualConnectivity(selectedTables, selectedIds, areTablesConnectedFn) {
  const ts = Array.isArray(selectedTables) ? selectedTables : [];
  if (ts.length <= 1) return { ok: true, reason: '' };
  const sameArea = ts.every(t => t.area === ts[0].area);
  if (!sameArea) return { ok: false, reason: 'Tische sind in unterschiedlichen Bereichen.' };
  const allExtendable = ts.every(t => t.extendable !== false);
  if (!allExtendable) return { ok: false, reason: 'Ein gewählter Tisch ist fix und kann nicht kombiniert werden.' };
  const connectedFn = typeof areTablesConnectedFn === 'function' ? areTablesConnectedFn : () => true;
  if (!connectedFn(Array.isArray(selectedIds) ? selectedIds : [])) {
    return { ok: false, reason: 'Die gewählten Tische stehen nicht nah genug beieinander.' };
  }
  return { ok: true, reason: '' };
}

/**
 * Pure-Logic für die Sitzplatz-Aggregation im Manual-Modus. Liefert ein
 * Snapshot-Objekt; der Aufrufer schreibt chairBonus zurück in den Picker-State.
 *
 * S207-HOTFIX — Auto-Zusatzstühle: ist `manualChairs` null/undefined/'' (der Operator
 * hat den Stepper nicht angefasst), zieht die Logik so viele hinterlegte Zusatzstühle
 * wie NÖTIG, um die Gästezahl zu decken (gedeckelt auf die verfügbaren `maxExtraChairs`).
 * So erkennt der Manuell-Modus — wie die Vorschlags-Engine — dass ein 3-Sitzer mit
 * hinterlegtem Zusatzstuhl 4 Gäste fasst, ohne dass der Operator den Stuhl erst manuell
 * hochdrehen muss. Kommt eine Zahl herein (Operator hat gewählt), gilt exakt diese
 * (auf [0, maxExtraChairs] geclamped) — dann kann er den Auto-Wert auch bewusst senken.
 * @param {Array<{seats: number}>} selectedTables
 * @param {number|string|null|undefined} manualChairs Eingabewert (null/undefined = Auto)
 * @param {number} requiredGuests Reservation-Gäste-Anzahl
 * @param {(table: any) => number} getExtraChairsFn injizierbar (capacity.tableExtraChairs)
 * @returns {{totalSeats: number, maxExtraChairs: number, chairBonus: number,
 *           effective: number, enough: boolean}}
 */
function _computeManualSeatTotals(selectedTables, manualChairs, requiredGuests, getExtraChairsFn) {
  const ts = Array.isArray(selectedTables) ? selectedTables : [];
  const extraFn = typeof getExtraChairsFn === 'function' ? getExtraChairsFn : () => 0;
  const totalSeats = ts.reduce((s, t) => s + (Number(t && t.seats) || 0), 0);
  const maxExtraChairs = ts.reduce((s, t) => s + (Number(extraFn(t)) || 0), 0);
  const required = Number(requiredGuests) || 0;
  let chairBonus;
  if (manualChairs === null || manualChairs === undefined || manualChairs === '') {
    // Auto: minimal nötige Zusatzstühle, um die Gästezahl zu erreichen.
    const needed = Math.max(0, required - totalSeats);
    chairBonus = Math.min(maxExtraChairs, needed);
  } else {
    const manual = Number(manualChairs) || 0;
    chairBonus = Math.max(0, Math.min(maxExtraChairs, manual));
  }
  const effective = totalSeats + chairBonus;
  return { totalSeats, maxExtraChairs, chairBonus, effective, enough: effective >= required };
}

/**
 * Submit-Button valid-Check für den Manual-Modus.
 * @returns {boolean}
 */
function _validatePickerSelection(selLength, enough, connectivityOk) {
  return !!enough && !!connectivityOk && Number(selLength) > 0;
}

// Backward-Compat
if (typeof window !== 'undefined') {
  Object.assign(window, {
    openAssignmentPicker,
    renderAssignmentPicker,
    togglePickerTable,
    setPickerChairs,
    setPickerPlanArea,
    acceptPickerManual,
    // S220 — detached-Kontext Dismiss/Back-Dispatcher (walkin.js create-on-accept)
    pickerHeaderDismiss,
    pickerActionsCancel,
    pickerBack
  });
  // _pickerState wird vom Walk-In-Modul vorgewählt — als getter/setter exposen
  Object.defineProperty(window, '_pickerState', {
    get() { return _pickerState; },
    set(v) { _pickerState = v; },
    configurable: true
  });
}

// ---------------------------------------------------------------
// Test-Hook-Exports (Session 39, Pattern aus customer-form.js).
// Zur Laufzeit No-Op.
// ---------------------------------------------------------------
export {
  _validateManualConnectivity,
  _computeManualSeatTotals,
  _validatePickerSelection,
  pickerTileStatus,          // S207 — Kachel-Status-Ableitung (frei/belegt/gewählt/empfehlung/wunsch)
  _resolvePickerPlanArea,    // S207 — aktiver Plan-Bereich (Wunsch-Vorwahl)
  _selectedTablesArea,       // S219-HF — gemeinsamer Bereich der gewählten Tische
  _tileCapacity,             // S207-HOTFIX — Kachel-Kapazität = Sitze + Zusatzstühle
  // _preselectTables ist bereits `export function` (S207-HOTFIX 4 Vorauswahl-Weiche)
};
